// Today / Home — the actual landing dashboard. Replaces the previous redirect
// straight to /inbox.
//
// Composition:
//   - Greeting banner (good morning/afternoon, your name, today's date)
//   - 6-column KPI ticker with deltas
//   - 3-column hot lists: replies, top contacts, deals (or dormant hot when no deals)
//   - Expiring contracts call-out

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Inbox, Users, Briefcase, FileSignature,
  TrendingUp, AlertCircle, Mail, Activity, Clock, Building2,
  Sparkles, RefreshCw, Loader2, Flame,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { homeApi, aiApi, contactsApi } from "@/lib/api";
import { cn, initials, avatarColor } from "@/lib/utils";
import { fmtMoneyDollars, fmtRelative, fmtNumber, pillVariant } from "@/lib/format";
import { useTitle } from "@/hooks/useTitle";
import { CONTRACT_BUILDER_ENABLED } from "@/lib/flags";

const greetingFor = (hour: number) =>
  hour < 5 ? "Working late" : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

const todayLabel = () =>
  new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

export default function HomeRoute() {
  useTitle("Today");
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["home"],
    queryFn: () => homeApi.get(),
    refetchInterval: 60_000,
  });

  return (
    <div className="h-full overflow-y-auto bg-bg-app scrollbar-thin">
      <div className="mx-auto max-w-7xl px-6 py-6">
        {/* Greeting + workspace pulse line — MASTER_PROMPT §4.1 */}
        <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
              {data ? `${greetingFor(data.greeting.hour)}, ${data.greeting.name}` : "Hello"}
            </h1>
            <p className="mt-1 text-base text-text-2">
              {todayLabel()}
              {data?.kpis && (
                <span className="ml-1 text-text-3">
                  {" · "}
                  {data.kpis.tasks_overdue > 0 && (
                    <span className="text-text-2">
                      <span className="font-medium text-warn">{data.kpis.tasks_overdue}</span> tasks overdue
                    </span>
                  )}
                  {data.kpis.tasks_overdue > 0 && data.kpis.replies_7d > 0 && " · "}
                  {data.kpis.replies_7d > 0 && (
                    <span className="text-text-2">
                      <span className="font-medium text-text">{data.kpis.replies_7d}</span> replies last 7d
                    </span>
                  )}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => navigate("/inbox")}>
              <Inbox className="h-3.5 w-3.5" /> Open inbox
            </Button>
            {CONTRACT_BUILDER_ENABLED && (
              <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => navigate("/contracts/templates")}>
                <FileSignature className="h-3.5 w-3.5" /> New contract
              </Button>
            )}
            <Button size="sm" variant="primary" className="gap-1.5" onClick={() => navigate("/contacts")}>
              <Users className="h-3.5 w-3.5" /> Contacts
            </Button>
          </div>
        </header>

        {error && (
          <div className="mb-4 rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
            Couldn't load dashboard: {(error as Error).message}
          </div>
        )}

        {/* Lumen Focus + My Watchlist — 2/3 + 1/3 on wide screens,
            stacked on narrow. The watchlist scales: top 8 owned
            fire/hot contacts, with a "See all" link for the rest. */}
        <div className="mb-6 grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2"><LumenFocusCard /></div>
          <WatchlistCard />
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          <KpiTile
            icon={<Users className="h-3.5 w-3.5" />}
            label="Contacts"
            value={data?.kpis.contacts_total}
            sub={data?.kpis.contacts_new_7d ? `+${data.kpis.contacts_new_7d} this week` : "in CRM"}
            trend={data?.kpis.contacts_new_7d ? "up" : "flat"}
            isLoading={isLoading}
            onClick={() => navigate("/contacts")}
          />
          <KpiTile
            icon={<Building2 className="h-3.5 w-3.5" />}
            label="Companies"
            value={data?.kpis.companies_total}
            sub="mirrored"
            isLoading={isLoading}
            onClick={() => navigate("/companies")}
          />
          <KpiTile
            icon={<Mail className="h-3.5 w-3.5" />}
            label="Replies (7d)"
            value={data?.kpis.replies_7d}
            sub={data?.kpis.replies_7d ? "follow up" : "from outreach"}
            trend={data?.kpis.replies_7d ? "up" : "flat"}
            highlight={!!data?.kpis.replies_7d}
            isLoading={isLoading}
            onClick={() => navigate("/inbox")}
          />
          <KpiTile
            icon={<Activity className="h-3.5 w-3.5" />}
            label="In sequences"
            value={data?.kpis.active_enrollments}
            sub="active"
            isLoading={isLoading}
            onClick={() => navigate("/sequences")}
          />
          <KpiTile
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Tasks overdue"
            value={data?.kpis.tasks_overdue}
            sub="overdue"
            trend={data?.kpis.tasks_overdue ? "danger" : "flat"}
            isLoading={isLoading}
            onClick={() => navigate("/tasks?tab=overdue")}
          />
        </div>

        {/* Hot lists row */}
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Hot threads */}
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4 text-accent-purple" />
                Latest replies
              </CardTitle>
              <Link to="/inbox" className="text-xs text-brand-deep hover:underline">
                Inbox →
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {isLoading ? (
                <ListSkeleton count={3} />
              ) : data?.hot_threads.length ? (
                data.hot_threads.map((t) => (
                  <Link
                    key={t.id}
                    to={`/inbox/${encodeURIComponent(t.id)}`}
                    className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                  >
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback className={avatarColor(t.contact_email ?? t.contact_name)}>
                        {initials(t.contact_name ?? t.contact_email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">
                        {t.contact_name?.trim() || t.contact_email || "Unknown"}
                      </p>
                      <p className="truncate text-[11px] text-ink-dim">
                        replied · {t.subject ?? "(no subject)"}
                      </p>
                    </div>
                    <span className="shrink-0 text-[10px] text-ink-dim">
                      {fmtRelative(t.last_message_at)}
                    </span>
                  </Link>
                ))
              ) : (
                <EmptyMini icon={<Mail className="h-4 w-4" />} text="No new replies yet" />
              )}
            </CardContent>
          </Card>

          {/* Top contacts */}
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4 text-brand" />
                Top contacts
              </CardTitle>
              <Link to="/contacts" className="text-xs text-brand-deep hover:underline">
                All contacts →
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {isLoading ? (
                <ListSkeleton count={3} />
              ) : data?.hot_contacts.length ? (
                data.hot_contacts.map((c) => (
                  <Link
                    key={c.id}
                    to={`/contacts/${c.id}`}
                    className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                  >
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback className={avatarColor(c.email)}>
                        {initials(c.full_name ?? c.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">
                        {c.full_name?.trim() || c.email}
                      </p>
                      <p className="truncate text-[11px] text-ink-dim">{c.company_name ?? "—"}</p>
                    </div>
                    <ScoreBadge score={c.rule_score} bucket={c.score_bucket} />
                  </Link>
                ))
              ) : (
                <EmptyMini icon={<Users className="h-4 w-4" />} text="No scored contacts yet" />
              )}
            </CardContent>
          </Card>

          {/* Hot deals OR dormant hot contacts */}
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                {data?.hot_deals.length ? (
                  <>
                    <Briefcase className="h-4 w-4 text-success" /> Pipeline focus
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-warn" /> Re-touch today
                  </>
                )}
              </CardTitle>
              <Link
                to={data?.hot_deals.length ? "/deals" : "/contacts"}
                className="text-xs text-brand-deep hover:underline"
              >
                {data?.hot_deals.length ? "All deals →" : "Contacts →"}
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {isLoading ? (
                <ListSkeleton count={3} />
              ) : data?.hot_deals.length ? (
                data.hot_deals.map((d) => (
                  <Link
                    key={d.id}
                    to="/deals"
                    className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                  >
                    <span
                      className="h-7 w-1 shrink-0 rounded-full"
                      style={{ backgroundColor: d.stage_color ?? "#94a3b8" }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                      <p className="truncate text-[11px] text-ink-dim">
                        {d.company_name ?? "—"} · {d.stage_name ?? "—"} · {d.days_in_stage ?? 0}d
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-brand-deep">
                      {fmtMoneyDollars(d.amount_usd)}
                    </span>
                  </Link>
                ))
              ) : data?.dormant_hot_contacts.length ? (
                data.dormant_hot_contacts.map((c) => {
                  const days = c.last_emailed_at
                    ? Math.floor((Date.now() - Date.parse(c.last_emailed_at)) / 86_400_000)
                    : null;
                  return (
                    <Link
                      key={c.id}
                      to={`/contacts/${c.id}`}
                      className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                    >
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarFallback className={avatarColor(c.email)}>
                          {initials(c.full_name ?? c.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">
                          {c.full_name?.trim() || c.email}
                        </p>
                        <p className="truncate text-[11px] text-ink-dim">
                          {c.company_name ?? "—"} ·{" "}
                          {days != null ? `quiet ${days}d` : "never emailed"}
                        </p>
                      </div>
                      <ScoreBadge score={c.rule_score} bucket={null} />
                    </Link>
                  );
                })
              ) : (
                <EmptyMini icon={<Briefcase className="h-4 w-4" />} text="No deals or dormant hot contacts" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Expiring contracts — hidden while the contract builder is archived */}
        {CONTRACT_BUILDER_ENABLED && data && data.expiring_contracts.length > 0 && (
          <Card className="mt-4">
            <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSignature className="h-4 w-4 text-warn" />
                Expiring in next 30 days
              </CardTitle>
              <Link to="/contracts?tab=expiring" className="text-xs text-brand-deep hover:underline">
                All expiring →
              </Link>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              {data.expiring_contracts.map((k) => (
                <Link
                  key={k.id}
                  to={`/contracts/${k.id}`}
                  className="flex items-center gap-2 rounded px-2 py-2 transition-colors hover:bg-surface-hover"
                >
                  <FileSignature className="h-4 w-4 shrink-0 text-ink-dim" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{k.title}</p>
                    <p className="truncate text-[11px] text-ink-dim">{k.company_name ?? "—"}</p>
                  </div>
                  <span className="shrink-0 text-xs text-warn">expires {fmtRelative(k.expires_at)}</span>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function KpiTile({
  icon, label, value, sub, trend = "flat", highlight, isLoading, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  sub: string;
  trend?: "up" | "flat" | "warn" | "danger";
  highlight?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
}) {
  const trendColor =
    trend === "up" ? "text-success" :
    trend === "warn" ? "text-warn" :
    trend === "danger" ? "text-danger" :
    "text-ink-dim";
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-lg border bg-surface-panel p-3 text-left shadow-sm transition-all hover:border-brand/40 hover:shadow-md",
        highlight ? "border-brand/30 bg-brand/5" : "border-line",
      )}
    >
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-ink-dim">
        <span className="flex items-center gap-1">{icon} {label}</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        {isLoading ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <span className="font-display text-2xl font-bold tabular-nums text-ink">
            {fmtNumber(value)}
          </span>
        )}
      </div>
      <p className={cn("mt-0.5 text-[11px]", trendColor)}>{sub}</p>
    </button>
  );
}

function ScoreBadge({ score, bucket }: { score: number | null; bucket: string | null }) {
  if (score == null) return <span className="text-xs text-ink-dim">—</span>;
  return (
    <Badge variant={pillVariant(bucket ?? "")} className="tabular-nums">
      {score}
    </Badge>
  );
}

function ListSkeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-2 px-2 py-2">
          <Skeleton className="h-7 w-7 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </>
  );
}

function EmptyMini({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 px-2 py-6 text-xs text-ink-dim">
      {icon} {text}
    </div>
  );
}

// ─── My Watchlist ──────────────────────────────────────────────────────
//
// Right-side card on the home dashboard. Lists the operator's OWNED
// contacts in the fire or hot score bucket, sorted by score desc then
// last-touch desc. Caps the visible list at 8 — beyond that the "See
// all" link goes to the full filtered contacts page. Scales without
// crowding the dashboard.

function WatchlistCard() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["home", "watchlist"],
    queryFn: () => contactsApi.list({
      mine: true,
      score: ["fire", "hot"],
      sort: "rule_score",
      dir: "desc",
      limit: 8,
    }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const rows = data?.contacts ?? [];
  const total = data?.total ?? 0;
  const hasMore = total > rows.length;

  return (
    <section
      className="rounded-xl border p-4 shadow-sm"
      style={{
        background: "var(--surface-panel)",
        borderColor: "var(--line)",
      }}
    >
      <div className="mb-3 flex items-center gap-2">
        <Flame className="h-4 w-4" style={{ color: "#f97316", fill: "#fb923c" }} />
        <h2 className="font-display text-sm font-semibold tracking-tight text-text">My watchlist</h2>
        <span className="text-[10.5px] text-text-3 font-mono">
          {total > 0 ? `${total}` : ""}
        </span>
        {hasMore && (
          <Link
            to="/contacts?mine=true&score=fire,hot"
            className="ml-auto text-[11px] text-text-3 hover:text-text"
          >
            See all
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : error ? (
        <p className="text-xs text-danger">
          Couldn't load watchlist: {(error as Error).message}
        </p>
      ) : rows.length === 0 ? (
        <div className="rounded border border-dashed border-line p-3 text-xs text-text-3">
          No fire/hot contacts assigned to you yet. As leads come in and you take ownership, they'll appear here.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((c) => (
            <WatchlistRow
              key={c.id}
              id={c.id}
              name={c.full_name ?? c.email ?? "(no name)"}
              email={c.email ?? null}
              score={c.rule_score ?? 0}
              bucket={c.score_bucket ?? null}
              vertical={c.vertical ?? null}
              lastEngagedAt={c.last_engaged_at ?? null}
              onOpen={() => navigate(`/contacts/${c.id}`)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function WatchlistRow({
  id, name, email, score, bucket, vertical, lastEngagedAt, onOpen,
}: {
  id: string;
  name: string;
  email: string | null;
  score: number;
  bucket: string | null;
  vertical: string | null;
  lastEngagedAt: string | null;
  onOpen: () => void;
}) {
  void id;
  const isFire = bucket === "fire";
  const seed = email ?? name;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-2.5 rounded-md border border-line bg-surface-panel2 px-2.5 py-2 text-left hover:bg-surface-hover"
      >
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className={cn("text-[10px] font-semibold", avatarColor(seed))}>
            {initials(name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[12.5px] font-medium text-text">{name}</span>
            <Badge
              variant={isFire ? "default" : "muted"}
              className="gap-0.5 shrink-0 px-1.5 py-0 text-[9.5px]"
              title={`rule_score ${score}`}
            >
              {isFire ? (
                <Flame className="h-2.5 w-2.5" style={{ color: "#f97316", fill: "#fb923c" }} />
              ) : null}
              {bucket ?? "—"}
            </Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-text-3">
            <span className="truncate">{vertical ?? "no vertical"}</span>
            {lastEngagedAt && (
              <>
                <span>·</span>
                <span className="whitespace-nowrap font-mono">{fmtRelative(lastEngagedAt)}</span>
              </>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

// ─── Lumen Focus Card ─────────────────────────────────────────────────
//
// Top-of-dashboard AI prompt. Calls /crm/api/ai/morning-brief which
// generates a per-day cached brief from open tasks + hot replies +
// hot contacts + open deals + closed-won MTD + unread inbox + the
// hot-followup queue. Cache key includes the user id + YYYY-MM-DD, so
// the first render of the day is the only one that pays the LLM call;
// every subsequent load hits the cache.

function LumenFocusCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ai", "morning-brief"],
    queryFn: () => aiApi.morningBrief(),
    staleTime: 30 * 60_000, // 30min — server-side cache is per-day; refresh sparingly
  });
  const regen = useMutation({
    // force=1 bypasses the per-day server-side cache so the operator
    // can re-roll if the day's snapshot changed (e.g. a new hot lead
    // landed).
    mutationFn: () => aiApi.morningBrief({ force: true }),
    onSuccess: (fresh) => qc.setQueryData(["ai", "morning-brief"], fresh),
  });
  const briefText = data?.content ?? "";
  const failed = data && data.ok === false;

  return (
    <section
      className="mb-6 rounded-xl border p-5 shadow-sm"
      style={{
        background: "linear-gradient(135deg, var(--email-ai-soft, rgba(120,90,240,0.07)) 0%, transparent 70%)",
        borderColor: "var(--email-ai-soft, rgba(120,90,240,0.25))",
      }}
    >
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4" style={{ color: "var(--email-ai, #7c5cff)" }} />
        <h2 className="font-display text-base font-semibold tracking-tight text-text">
          Focus today
        </h2>
        <span className="text-[11px] text-text-3">from Lumen</span>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-7 gap-1.5 px-2 text-xs"
          disabled={regen.isPending}
          onClick={() => regen.mutate()}
          title="Regenerate the brief now"
        >
          {regen.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : error ? (
        <p className="text-sm text-danger">
          Couldn't load the brief: {(error as Error).message}
        </p>
      ) : failed ? (
        <p className="text-sm text-warn">
          Brief couldn't be generated: {data?.error ?? data?.status ?? "unknown error"}.
        </p>
      ) : (
        <>
          <div className="lumen-brief space-y-1.5 text-sm leading-relaxed text-text">
            {renderBriefMarkdown(briefText)}
          </div>
          {data?.from_cache && data.generated_at && (
            <p className="mt-2 text-[10.5px] text-text-3 font-mono">
              cached · generated {fmtRelative(data.generated_at)}
            </p>
          )}
        </>
      )}
    </section>
  );
}

// Lightweight markdown renderer for the brief — handles **bold**, line
// breaks, and the "1. **action** — reason" numbered list pattern the
// system prompt enforces. Avoids pulling in a full markdown lib for
// 50 lines of output.
function renderBriefMarkdown(md: string): React.ReactNode {
  if (!md.trim()) return <p className="text-text-3">Nothing on fire — quiet day.</p>;
  const lines = md.split("\n").filter((l) => l.trim().length > 0);
  return lines.map((line, i) => {
    // Numbered priority items
    const numbered = /^(\d+)\.\s+(.*)$/.exec(line);
    if (numbered) {
      return (
        <div key={i} className="flex gap-3">
          <span className="shrink-0 font-mono text-[12px] text-text-3 pt-0.5">{numbered[1]}.</span>
          <p className="flex-1">{renderInlineMarkdown(numbered[2])}</p>
        </div>
      );
    }
    // Header lines (Today, Monday, ...)
    if (line.startsWith("**") && line.endsWith("**")) {
      return (
        <p key={i} className="text-text-2 font-medium">{renderInlineMarkdown(line)}</p>
      );
    }
    return <p key={i}>{renderInlineMarkdown(line)}</p>;
  });
}

// Tokenizes the three inline markups we emit from the brief prompt:
//   1. [Name](contact:id) — server-side instruction, becomes a React
//      Router <Link> so navigation stays client-side (no full reload).
//   2. **bold**            — semantic bold.
//   3. `code`              — inline mono.
//
// Earlier this was a single greedy alternation regex. When the brief
// output wrapped an action in `**...**` AND the action embedded a
// `[Robert](contact:ct_xxx)` link, the bold capture swallowed the entire
// span (including the link tokens) and rendered the raw markdown as a
// bolded literal. The split-by-`**` approach below separates bold
// boundaries first, then tokenizes links + code INSIDE each segment, so
// a link nested under bold renders as <strong><Link>Robert</Link></strong>.
function renderInlineMarkdown(input: string): React.ReactNode[] {
  const segments = input.split(/\*\*/);
  const out: React.ReactNode[] = [];
  const idRef = { n: 0 };

  for (let segIdx = 0; segIdx < segments.length; segIdx++) {
    const seg = segments[segIdx];
    if (!seg) continue;
    const isBold = segIdx % 2 === 1;
    const tokens = tokenizeLinkAndCode(seg, idRef);
    if (isBold) {
      out.push(
        <strong key={`b-${idRef.n++}`} className="font-semibold">
          {tokens}
        </strong>,
      );
    } else {
      out.push(...tokens);
    }
  }
  return out;
}

function tokenizeLinkAndCode(input: string, idRef: { n: number }): React.ReactNode[] {
  const TOKEN_RE = /\[([^\]]+)\]\(contact:([^)]+)\)|`([^`]+)`/g;
  const out: React.ReactNode[] = [];
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = TOKEN_RE.exec(input)) !== null) {
    if (m.index > cursor) out.push(input.slice(cursor, m.index));
    if (m[1] && m[2]) {
      out.push(
        <Link
          key={`l-${idRef.n++}`}
          to={`/contacts/${encodeURIComponent(m[2])}`}
          className="font-semibold text-brand underline-offset-2 hover:underline"
        >
          {m[1]}
        </Link>,
      );
    } else if (m[3]) {
      out.push(
        <code key={`c-${idRef.n++}`} className="rounded bg-surface-panel2 px-1 text-[12px] font-mono">
          {m[3]}
        </code>,
      );
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < input.length) out.push(input.slice(cursor));
  return out;
}
